// src/components/ChargingStation/hooks/useStripeWeb.ts
import { useEffect, useRef, useState } from 'react';
import {
  loadStripe,
  Stripe,
  StripeAddressElement,
  StripeElements,
  StripePaymentElement,
  StripePaymentElementOptions,
} from '@stripe/stripe-js';
import { notificationService } from '../../../services/notification-service';
import { createOrder, cancelOrder } from '../../../services/orders-service';
import { isSuccessful } from '../../../services/api-requests';
import { getPaymentDetails } from '../../../services/payments-service';
import { ChargerAttributes } from '../../../models/charger-model';
import { OrderAttributes } from '../../../models/order-model';
import { PaymentDetails } from '../../../models/payment-model';

interface UseStripeWebOptions {
  selectedCharger: ChargerAttributes | null | undefined;
  onNewOrderCreated: (o: OrderAttributes) => void;
}

export function useStripeWeb({
  selectedCharger,
  onNewOrderCreated,
}: UseStripeWebOptions) {
  // ────────────────────────────────────────────────────────────────────────────────
  // State
  // ────────────────────────────────────────────────────────────────────────────────
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [stripeElements, setStripeElements] = useState<
    StripeElements | undefined
  >(undefined);
  const [stripePaymentElement, setStripePaymentElement] = useState<
    StripePaymentElement | undefined
  >(undefined);
  const [stripeAddressElement, setStripeAddressElement] = useState<
    StripeAddressElement | undefined
  >(undefined);

  const [order, setOrder] = useState<OrderAttributes | null>(null);
  const [paymentData, setPaymentData] = useState<PaymentDetails | null>(null);
  const [paymentsDropdownOpen, setPaymentsDropdownOpen] =
    useState<boolean>(false);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [chargerInUse, setChargerInUse] = useState<boolean>(false);

  // Track last order id and debounce rapid updates
  const lastOrderIdRef = useRef<string | null>(null);
  const setOrderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track if order has been created to prevent duplicates
  const hasCreatedOrderRef = useRef<boolean>(false);

  const scheduleOrderUpdate = (next: OrderAttributes | null) => {
    if (setOrderTimerRef.current) {
      clearTimeout(setOrderTimerRef.current);
    }
    setOrderTimerRef.current = setTimeout(() => {
      if (next === null) {
        if (lastOrderIdRef.current !== null) {
          lastOrderIdRef.current = null;
          setOrder(null);
        }
        return;
      }
      if (lastOrderIdRef.current === next.id) {
        return; // equality guard by id
      }
      lastOrderIdRef.current = next.id;
      setOrder(next);
    }, 50);
  };

  // ────────────────────────────────────────────────────────────────────────────────
  // Helpers
  // ────────────────────────────────────────────────────────────────────────────────
  async function createOrderForCharger(): Promise<OrderAttributes | null> {
    if (!selectedCharger?.uuid) return null;
    console.log('[useStripeWeb] Creating order for charger:', selectedCharger.uuid);
    const response = await createOrder(selectedCharger.uuid);
    
    // Check if charger is in use (status 204 - No Content)
    if (response.status === 204) {
      console.log('[useStripeWeb] Charger is currently in use, cannot create order');
      setChargerInUse(true);
      return null;
    }
    
    if (isSuccessful(response.status)) {
      console.log('[useStripeWeb] Order created successfully:', response.payload?.id);
      setChargerInUse(false);
      return response.payload!;
    }
    
    console.error('[useStripeWeb] Order creation failed:', response);
    return null;
  }

  async function fetchStripeInstance(): Promise<Stripe | null> {
    try {
      const s = await loadStripe(process.env.STRIPE_PUBLISHABLE_KEY!);
      return s;
    } catch (err) {
      console.error('Error loading Stripe:', err);
      setLoadError(true);
      return null;
    }
  }

  /**
   * Automatically use existing payment method if available
   */
  async function useExistingPaymentMethod(): Promise<void> {
    if (!stripe || !stripeElements || !order) {
      console.warn('Missing required data for automatic payment submission');
      return;
    }

    // If payment data already exists, we don't need to do anything
    if (paymentData) {
      console.log('Payment data already exists, no need for automatic payment');
      return;
    }

    try {
      console.log(
        'Automatically using existing payment method for order:',
        order.id
      );

      const confirmation = await stripe.confirmPayment({
        elements: stripeElements,
        clientSecret: order.payment.clientSecret,
        redirect: 'if_required',
        confirmParams: {},
      });

      console.log('Automatic payment confirmation result:', confirmation);

      // Handle payment confirmation status
      switch (confirmation?.paymentIntent?.status) {
        case 'requires_capture': {
          const paymentDetailsResponse = await getPaymentDetails(order.id);
          // Update payment data and UI state
          setPaymentData(paymentDetailsResponse.payload!);
          console.log('Automatic payment successful, payment data updated');
          break;
        }
        default:
          console.log(
            'Unhandled automatic payment status:',
            confirmation?.paymentIntent?.status
          );
      }
    } catch (err) {
      console.error('Error during automatic payment submission:', err);
      // If automatic payment fails, we don't want to block the user
      // They can still manually add/change payment method
    }
  }

  function mountElements(o: OrderAttributes, s: Stripe) {
    const elements = s.elements({
      clientSecret: o.payment.clientSecret,
      customerSessionClientSecret: o.payment.customerSessionClientSecret,
      appearance: {
        theme: 'night',
        variables: {
          colorPrimary: '#3FC67D',
          colorBackground: '#E8E8E9',
          colorText: '#000000',
          colorTextSecondary: '#808080',
          colorTextPlaceholder: '#808080',
          colorDanger: '#ff0000',
        },
        labels: 'floating',
        rules: {
          '.CheckboxLabel': {
            color: '#808080',
          },
        },
      },
    });

    setStripeElements(elements);
    const paymentEl = elements.create(
      'payment',
      {} as StripePaymentElementOptions
    );
    const addressEl = elements.create('address', {
      mode: 'billing',
      allowedCountries: ['US'],
    });

    setStripePaymentElement(paymentEl);
    setStripeAddressElement(addressEl);
  }

  // ────────────────────────────────────────────────────────────────────────────────
  // Create + Load Stripe + Elements on mount (Web only)
  // ────────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof window === 'undefined' || !window.document) return;

    // Guard: if selectedCharger is null/undefined, don't create order
    if (!selectedCharger?.uuid) {
      console.log('[useStripeWeb] No selectedCharger, skipping order creation');
      return;
    }

    // Prevent duplicate order creation by checking if we already have an order
    if (order && order.chargerId === selectedCharger.uuid) {
      console.log('[useStripeWeb] Order already exists for this charger, skipping creation');
      return;
    }

    // Additional guard: prevent re-run during React Strict Mode double-invoke
    if (hasCreatedOrderRef.current) {
      console.log('[useStripeWeb] Order creation already in progress, skipping...');
      return;
    }

    // Mark that we're creating an order (before async operation starts)
    hasCreatedOrderRef.current = true;

    let orderData: OrderAttributes | null = null;
    let stripeInstance: Stripe | null = null;

    // 1) Create order, then fetch Stripe, then mount Elements
    createOrderForCharger()
      .then((o) => {
        if (!o) {
          // Check if it's because charger is in use (we set chargerInUse state)
          // If so, don't set loadError - we handle this gracefully
          if (!chargerInUse) {
            setLoadError(true);
          }
          hasCreatedOrderRef.current = false; // Reset on failure
          return null; // Return null to stop the chain
        }
        
        orderData = o;
        scheduleOrderUpdate(o);
        onNewOrderCreated(o);

        return fetchStripeInstance();
      })
      .then((s) => {
        if (!s) {
          setLoadError(true);
          hasCreatedOrderRef.current = false; // Reset on failure
          return;
        }
        stripeInstance = s;
        setStripe(s);

        // Now mount elements with the order and stripe instance
        if (orderData && stripeInstance) {
          // Create elements and store them in state
          const elements = stripeInstance.elements({
            clientSecret: orderData.payment.clientSecret,
            customerSessionClientSecret:
              orderData.payment.customerSessionClientSecret,
            appearance: {
              theme: 'night',
              variables: {
                colorPrimary: '#3FC67D',
                colorBackground: '#E8E8E9',
                colorText: '#000000',
                colorTextSecondary: '#808080',
                colorTextPlaceholder: '#808080',
                colorDanger: '#ff0000',
              },
              labels: 'floating',
              rules: {
                '.CheckboxLabel': {
                  color: '#808080',
                },
              },
            },
          });

          setStripeElements(elements);
          const paymentEl = elements.create(
            'payment',
            {} as StripePaymentElementOptions
          );
          const addressEl = elements.create('address', {
            mode: 'billing',
            allowedCountries: ['US'],
          });

          setStripePaymentElement(paymentEl);
          setStripeAddressElement(addressEl);

          // Mount the elements immediately for automatic dropdown
          console.log('Mounting elements immediately for automatic dropdown');
          try {
            addressEl.mount('#address-element');
            paymentEl.mount('#payment-element');
            setPaymentsDropdownOpen(true);
            console.log('Dropdown opened successfully');
          } catch (error) {
            console.error('Error mounting Stripe elements:', error);
          }
        }
      })
      .catch((err) => {
        console.error('Failed to load Stripe Elements or order:', err);
        setLoadError(true);
        // Reset flag on error so it can retry
        hasCreatedOrderRef.current = false;
      });
    
    // Note: DON'T reset hasCreatedOrderRef in cleanup!
    // That would allow React Strict Mode's double-invoke to create duplicate orders.
    // The ref persists across Strict Mode's unmount/remount cycle, which is what we want.
  }, [selectedCharger.uuid, order]);

  // Cleanup debouncer on unmount
  useEffect(() => {
    return () => {
      if (setOrderTimerRef.current) {
        clearTimeout(setOrderTimerRef.current);
      }
    };
  }, []);

  // ────────────────────────────────────────────────────────────────────────────────
  // Handlers exposed to the screen
  // ────────────────────────────────────────────────────────────────────────────────

  /**
   * Called when user opens/closes the "Add payment method" dropdown on web.
   */
  function onDropdownPressed() {
    console.log('onDropdownPressed called', {
      stripePaymentElement: !!stripePaymentElement,
      stripeAddressElement: !!stripeAddressElement,
      paymentsDropdownOpen,
      order: !!order,
    });

    if (!stripePaymentElement || !stripeAddressElement) {
      console.warn('Stripe elements not ready yet');
      return;
    }

    if (paymentsDropdownOpen) {
      // Close dropdown
      stripePaymentElement.unmount();
      stripeAddressElement.unmount();
      setPaymentsDropdownOpen(false);
    } else {
      // Open dropdown
      try {
        stripeAddressElement.mount('#address-element');
        stripePaymentElement.mount('#payment-element');
        setPaymentsDropdownOpen(true);
      } catch (error) {
        console.error('Error mounting Stripe elements:', error);
      }
    }
  }

  /**
   * When user clicks "Submit" on Stripe Elements form:
   */
  async function onPaymentSubmit(): Promise<void> {
    console.log('onPaymentSubmit called', {
      stripe: !!stripe,
      stripeElements: !!stripeElements,
      order: !!order,
      orderId: order?.id,
      clientSecret: order?.payment?.clientSecret,
    });

    if (!stripe || !stripeElements || !order) {
      console.warn('Missing required data for payment submission');
      return;
    }

    try {
      const confirmation = await stripe.confirmPayment({
        elements: stripeElements,
        clientSecret: order.payment.clientSecret,
        redirect: 'if_required',
        confirmParams: {},
      });

      console.log('Payment confirmation result:', confirmation);

      // Handle payment confirmation status
      switch (confirmation?.paymentIntent?.status) {
        case 'requires_capture': {
          const paymentDetailsResponse = await getPaymentDetails(order.id);
          // Update payment data and UI state
          setPaymentData(paymentDetailsResponse.payload!);
          setPaymentsDropdownOpen(false);
          console.log('Payment successful, payment data updated');
          break;
        }
        default:
          console.log(
            'Unhandled payment status:',
            confirmation?.paymentIntent?.status
          );
      }
    } catch (err) {
      console.error('Error during Stripe payment submission:', err);
    }
  }

  /**
   * If user clicks the "X" on the payment card, remove current payment method
   * and create a brand-new order.
   */
  async function handleRemovePayment() {
    if (!order) return;
    await cancelOrder(order.id);
    scheduleOrderUpdate(null);
    setPaymentData(null);
    onDropdownPressed(); // ensure elements are unmounted

    const newOrder = await createOrderForCharger();
    if (!newOrder) {
      setLoadError(true);
      return;
    }
    scheduleOrderUpdate(newOrder);
    onNewOrderCreated(newOrder);
  }

  /**
   * Automatically open payment dropdown for users with existing payment methods
   * This allows them to see their saved payment method and submit it immediately
   */
  async function openPaymentDropdownForExistingMethod(): Promise<void> {
    console.log(
      'openPaymentDropdownForExistingMethod called (legacy function)'
    );
    // This function is kept for compatibility but the logic is now in the useEffect
  }

  // ────────────────────────────────────────────────────────────────────────────────
  // Return everything the screen needs for "Web" payments
  // ────────────────────────────────────────────────────────────────────────────────
  return {
    // State
    isLoading: order === null && !loadError && !chargerInUse,
    order,
    paymentData,
    paymentsDropdownOpen,
    loadError,
    chargerInUse,
    reinitializeForNewOrder: (nextOrder: OrderAttributes) => {
      // Set your internal order state, update clientSecret/ephemeralKey, etc.
      // Example:
      setOrder(nextOrder);
      // setPaymentData({
      //   clientSecret: nextOrder.payment.clientSecret,   // adapt to your field names
      //   customerId:   nextOrder.payment.stripeCustomerId,
      //   ephemeralKey: nextOrder.payment.ephemeralKey,
      // });
    },

    // Elements
    stripe,
    stripeElements,
    stripePaymentElement,
    stripeAddressElement,

    // Handlers
    onDropdownPressed,
    onPaymentSubmit,
    handleRemovePayment,
    openPaymentDropdownForExistingMethod,
  };
}
